import { FormApi } from "final-form";
import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { CreateFdroidRepoPayload } from "@dashboard/repositories/api/rtkClient";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { InputAdornment } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import PasswordField from "@shared/form/PasswordField";
import { TextField } from "@shared/form/TextField";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { useCreateFdroidMutation } from "../api";
import { useStyles } from "../RepositoriesPage.style";

import { validationSchema } from "./CreateFdroidRepoDialog.validation";

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

type FormValues = Omit<CreateFdroidRepoPayload, "subscriptionId">;

export const CreateFdroidRepoDialog = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation(["modals", "common"]);

    const { classes } = useStyles();
    const { data: workspace } = useCurrentWorkspace();
    const subscriptionId = workspace?.subscription.id;
    const validate = useValidationSchema(validationSchema);
    const [createFdroid] = useCreateFdroidMutation();

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        if (subscriptionId) {
            setLoading(true);

            const payload: CreateFdroidRepoPayload = {
                subscriptionId,
                ...values,
            };

            createFdroid(payload)
                .unwrap()
                .then(() => {
                    form.restart();
                    showSuccessToast(t("modals:createRepository.toast.repositoryCreatedSuccess"));
                })
                .finally(() => {
                    setLoading(false);
                    props.onClose();
                });
        }
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:createRepository.title")}
                        description={t("modals:createRepository.description")}
                        open={props.isOpen}
                        onClose={props.onClose}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        disableEnforceFocus
                        endButton={
                            <PrimaryButton disabled={loading} loading={loading} onClick={form.submit}>
                                {t("common:button.create")}
                            </PrimaryButton>
                        }
                    >
                        <TextField
                            name="name"
                            label={t("modals:createRepository.input.name.caption")}
                            fullWidth
                            className={classes.adornment}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        // eslint-disable-next-line i18next/no-literal-string
                                        <InputAdornment position="start">https://apps.emteria.com/fdroid/</InputAdornment>
                                    ),
                                },
                            }}
                            required
                        />
                        <PasswordField
                            name="password"
                            label={t("common:label.password")}
                            placeholder={t("common:label.password")}
                            fullWidth
                        />
                        <TextField
                            name="title"
                            label={t("common:label.title")}
                            placeholder={t("common:label.title")}
                            fullWidth
                        />
                        <TextField
                            multiline
                            name="description"
                            label={t("common:label.description")}
                            placeholder={t("common:label.description")}
                            fullWidth
                        />
                    </Modal>
                </form>
            )}
        />
    );
};
